import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { AppState } from '@/types';

export function useStatePersistence(
  state: AppState,
  setAppState: (state: AppState) => void,
  isInitialized: boolean,
  loading: boolean
) {
  const { user } = useAuth();

  // Save state changes to Supabase (only after initialization and prevent infinite loops)
  useEffect(() => {
    // Check if data clearing is in progress
    const isClearing = (window as any).clearingData;
    if (isClearing) {
      if (import.meta.env.DEV) console.log('🚫 Skipping save - data clearing in progress');
      return;
    }
    
    if (user && !loading && isInitialized && state.isAuthenticated) {
      const hasNewActivities = state.activities.length > 0;
      const isLikelyStaleState = !hasNewActivities && state.totalPoints === 0 && isInitialized;
      
      if (isLikelyStaleState) {
        if (import.meta.env.DEV) console.log('⏸️ Skipping save - likely stale state during data load');
        return;
      }
      
      const saveData = async () => {
        try {
          if ((window as any).clearingData) return;
          await setAppState(state);
        } catch (error) {
          console.error('❌ Failed to save state to Supabase:', error);
        }
      };
      
      const timeoutId = setTimeout(saveData, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [
    state.totalPoints, 
    state.currentWeekPoints, 
    state.activities.length, 
    state.virtualPet?.happiness, 
    state.simplePet?.happiness,
    state.simplePet?.stage,
    state.simplePet?.totalPoints,
    state.challenges.length,
    state.habits.length,
    state.lastStreakDate,
    state.weeklyGoal,
    state.user?.weeklyGoal,
    state.user?.name,
    user?.id, 
    setAppState
  ]);
}
